import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Methodology-library surface (T-0111 / E01-F04 + F09).
 *
 * Reads the firm-level template library that backs the Firm Settings →
 * Methodology library screen. Two endpoints are exercised here, both from
 * the T-0025 backend (`apps/api/src/routes/templates.ts`):
 *
 *   - GET /api/v1/templates                — list templates (active by
 *                                             default; include_retired=1
 *                                             for retired rows too).
 *   - GET /api/v1/templates/:id/versions   — full append-only version
 *                                             history for one template,
 *                                             ordered ascending.
 *
 * Shapes mirror `apps/api/src/services/templates.ts → listTemplates /
 * listTemplateVersions`. Writes (POST author / publish / retire) are
 * firm-admin curation actions surfaced from this screen in a future
 * ticket; the v1 library page is read-only with the version + retire UI
 * affordances stubbed.
 *
 * The library hub also surfaces framework overlays, register schemas and
 * widget defaults sections — per the prototype `screens-firm.js →
 * firmLibrary()`. The TA backend currently exposes the three kinds via
 * the same `methodology_templates` table (no separate overlay /
 * register-schema list endpoint exists); the screen groups what it gets
 * back by a UI-derived `kind` heuristic on the template name + config so
 * the section layout matches the prototype.
 */

export const TemplateOriginSchema = z.enum(['human_authored', 'engagement_derived']);
export type TemplateOrigin = z.infer<typeof TemplateOriginSchema>;

export const TemplateStatusSchema = z.enum(['active', 'retired']);
export type TemplateStatus = z.infer<typeof TemplateStatusSchema>;

export const TemplateSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: TemplateStatusSchema,
  origin: TemplateOriginSchema,
});
export type TemplateSummary = z.infer<typeof TemplateSummarySchema>;

export const TemplateListSchema = z.array(TemplateSummarySchema);
export type TemplateList = z.infer<typeof TemplateListSchema>;

export const TemplateVersionSummarySchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  framework_definition_id: z.string().uuid(),
  published_at: z.string(),
  published_by: z.string().uuid(),
});
export type TemplateVersionSummary = z.infer<typeof TemplateVersionSummarySchema>;

export const TemplateVersionsResponseSchema = z.object({
  template: z.object({
    id: z.string().uuid(),
    name: z.string(),
    status: TemplateStatusSchema,
    origin: TemplateOriginSchema,
  }),
  versions: z.array(TemplateVersionSummarySchema),
});
export type TemplateVersionsResponse = z.infer<typeof TemplateVersionsResponseSchema>;

export interface ListTemplatesOpts {
  /** Include retired templates in the response. Backend default is false. */
  includeRetired?: boolean;
}

export function listTemplates(opts: ListTemplatesOpts = {}): Promise<TemplateList> {
  const query: Record<string, string | undefined> = {};
  if (opts.includeRetired) query.include_retired = '1';
  return apiClient('/api/v1/templates', TemplateListSchema, { query });
}

export function listTemplateVersions(templateId: string): Promise<TemplateVersionsResponse> {
  return apiClient(
    `/api/v1/templates/${encodeURIComponent(templateId)}/versions`,
    TemplateVersionsResponseSchema,
  );
}

export function useTemplatesQuery(opts: ListTemplatesOpts = {}) {
  return useQuery({
    queryKey: ['templates', 'list', opts.includeRetired ?? false] as const,
    queryFn: () => listTemplates(opts),
    staleTime: 30_000,
  });
}

export function useTemplateVersionsQuery(templateId: string | undefined) {
  return useQuery({
    queryKey: ['templates', 'versions', templateId] as const,
    queryFn: () => listTemplateVersions(templateId!),
    enabled: Boolean(templateId),
    staleTime: 30_000,
  });
}

/**
 * UI-only classification helper. The backend stores everything in the
 * single `methodology_templates` table; the prototype's library screen
 * groups templates into four "kinds" (Framework / Framework overlay /
 * Register schema / Widget defaults). v1 uses the template's `name`
 * as a heuristic; once the backend grows kind metadata this collapses to
 * a simple field read.
 */
export type LibraryKind = 'Framework' | 'Framework overlay' | 'Register schema' | 'Widget defaults';

export function inferLibraryKind(template: TemplateSummary): LibraryKind {
  const lowered = template.name.toLowerCase();
  if (lowered.includes('overlay')) return 'Framework overlay';
  if (lowered.includes('schema') || lowered.includes('register')) return 'Register schema';
  if (lowered.includes('widget')) return 'Widget defaults';
  return 'Framework';
}
